import { Box, Typography } from '@mui/material';
import Fuse from 'fuse.js';
import useDeviceGroupByDeviceId from 'hooks/useDeviceGroupByDeviceId';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import CustomSearchbar from 'components/CustomSearchBar';
import DeviceList from 'components/DeviceComponents/DeviceList';
import GroupNames from 'components/DeviceComponents/GroupNames';
import RightComponentWrapper from 'components/DeviceComponents/RightComponentWrapper';
import DeviceGroupsIcon from 'components/Icons/DeviceGroupsIcon';

import { useAppSelector } from 'redux-store';

interface PairDevicesProps {
  pairedDevices: number[] | false;
  selectedDeviceIds: number[];
  setSelectedDeviceIds: (selectedDeviceIds: number[]) => void;
}

const DeviceGroupData: FC<{ deviceId: number; serial: string }> = ({
  deviceId,
  serial,
}) => {
  const deviceGroups = useDeviceGroupByDeviceId(deviceId);

  if (!deviceGroups || deviceGroups.length === 0) return null;

  return (
    <RightComponentWrapper testId={`device-${serial}-groups`}>
      <DeviceGroupsIcon fontSize="small" sx={{ color: 'grey.500' }} />
      <Typography
        data-testid="device-group-names"
        variant="body1"
        noWrap
        color="grey.500"
      >
        <GroupNames items={deviceGroups} />
      </Typography>
    </RightComponentWrapper>
  );
};

const PairDevices: FC<PairDevicesProps> = ({
  pairedDevices,
  selectedDeviceIds,
  setSelectedDeviceIds,
}) => {
  const { t } = useTranslation();

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);

  const currentPairedDevices = useMemo(
    () => pairedDevices || [],
    [pairedDevices],
  );

  const deviceList = useAppSelector(s => s.devices.items);

  const unpairedDevicesList = useMemo(() => {
    return deviceList.filter(
      device => !currentPairedDevices.includes(device.id),
    );
  }, [deviceList, currentPairedDevices]);

  const chargers = unpairedDevicesList.filter(
    device => !device.type.toLowerCase().includes('controller'),
  );
  const fullStatus = useAppSelector(appState => appState.api.fullStatus);

  const devicesWithNames = useMemo(() => {
    return chargers.map(device => ({
      ...device,
      name: fullStatus[device.serial]?.fna,
    }));
  }, [chargers, fullStatus]);

  const fuse = useMemo(
    () =>
      new Fuse(devicesWithNames, {
        keys: ['name', 'serial'],
        threshold: 0.4,
      }),
    [devicesWithNames],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.serial));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  const filteredDevices = useMemo(
    () =>
      searchResults
        ? devicesWithNames.filter(device =>
            searchResults.includes(device.serial),
          )
        : devicesWithNames,
    [devicesWithNames, searchResults],
  );

  return (
    <Box>
      <Box mb={3} width="100%">
        <CustomSearchbar
          search={searchString}
          setSearch={setSearchString}
          hasSearched={searchString.length > 0}
          handleSearch={handleSearch}
          handleClearSearch={handleClearSearch}
        />
      </Box>
      {filteredDevices.length > 0 ? (
        filteredDevices.map(item => (
          <DeviceList
            key={item.id}
            deviceId={item.id}
            selectable
            serial={item.serial}
            selectedDeviceIds={selectedDeviceIds}
            setSelectedDeviceIds={setSelectedDeviceIds}
            rightComponent={
              <DeviceGroupData deviceId={item.id} serial={item.serial} />
            }
          />
        ))
      ) : (
        <Typography variant="body1" color="grey.600" textAlign="center">
          {t('common:pages.rfids.pair_devices.no_devices_found')}
        </Typography>
      )}
    </Box>
  );
};

export default PairDevices;
